import { onMounted, watch, nextTick } from 'vue';
import { useRoute } from 'vue-router';

export interface UseAnchorScrollOptions {
  /**
   * Optional fixed offset in pixels from the top of the viewport. When set,
   * it overrides the target's CSS `scroll-margin-top` (as read from
   * `getComputedStyle`).
   */
  offset?: number;

  /**
   * Delay applied to the first scroll-on-mount, giving the layout time to
   * paint before scrolling. Defaults to 100ms.
   */
  mountDelayMs?: number;
}

/**
 * Wires up deep-linking behaviour for documentation pages.
 *
 * 1. On mount, if the URL contains a `#section-id`, smooth-scroll to that
 *    element.
 * 2. When the route hash changes (e.g. another in-page anchor link is
 *    clicked), re-scroll to the new target.
 * 3. Returns a `scrollToSection(id)` helper for click handlers that should
 *    scroll a section into view AND update the URL hash so the location is
 *    deep-linkable / refresh-stable.
 *
 * Without an explicit `offset`, the composable computes the required offset
 * from the target's CSS `scroll-margin-top` (e.g. `.theme-anchor-target` in
 * `_theme-gradients.scss`). If the computed value cannot be parsed (e.g.
 * `calc()` in older browsers) it falls back to measuring the live `.page-header`
 * height plus a 24 px padding. This keeps click-scroll and direct-URL scroll
 * landing at the same vertical position. Pass `offset` only when you need a
 * fixed pixel value instead of the element's CSS scroll-margin.
 *
 * Usage:
 *
 * ```vue
 * <script setup>
 * import { useAnchorScroll } from '@/composables/common/useAnchorScroll';
 * const { scrollToSection } = useAnchorScroll();
 * </script>
 *
 * <template>
 *   <section id="onboarding" class="card theme-anchor-target">…</section>
 * </template>
 * ```
 */
export function useAnchorScroll(options: UseAnchorScrollOptions = {}) {
  const { mountDelayMs = 100, offset } = options;
  const route = useRoute();

  function getScrollOffset(element: HTMLElement): number {
    // Always measure the live header — it can be taller than the static
    // $header-size used in the CSS calc() (e.g. dynamic banners).
    const header = document.querySelector('.page-header');
    const liveOffset = header
      ? (header as HTMLElement).getBoundingClientRect().height + 24
      : 160;

    // Prefer the larger of the live measurement and the CSS value so we
    // never under-scroll and leave the target hidden under the header.
    const computed = getComputedStyle(element).scrollMarginTop;
    const pxMatch = computed.match(/^([\d.]+)px$/i);
    if (pxMatch) {
      const cssOffset = parseFloat(pxMatch[1]);
      if (Number.isFinite(cssOffset) && cssOffset > 0) {
        return Math.max(cssOffset, liveOffset);
      }
    }

    const parsed = parseFloat(computed);
    if (Number.isFinite(parsed) && parsed > 0) {
      return Math.max(parsed, liveOffset);
    }

    return liveOffset;
  }

  function scrollToElementById(id: string) {
    if (!id) return;
    const element = document.getElementById(id);
    if (!element) return;

    if (typeof offset === 'number') {
      const scrollY = window.scrollY ?? document.documentElement.scrollTop;
      const anchorTop = element.getBoundingClientRect().top + scrollY;
      window.scrollTo({ top: Math.max(0, anchorTop - offset), behavior: 'smooth' });
      return;
    }

    const scrollOffset = getScrollOffset(element);
    const scrollY = window.scrollY ?? document.documentElement.scrollTop;
    const anchorTop = element.getBoundingClientRect().top + scrollY;
    window.scrollTo({ top: Math.max(0, anchorTop - scrollOffset), behavior: 'smooth' });
  }

  function scrollToCurrentHashWithDelay() {
    setTimeout(() => {
      const hash = window.location.hash;
      if (!hash) return;
      scrollToElementById(hash.slice(1));
    }, mountDelayMs);
  }

  function scrollToSection(id: string) {
    if (!id) return;

    // Update the URL hash so the location stays deep-linkable / refresh-stable.
    // Use replaceState so we don't pollute the back/forward history with every
    // intra-page jump.
    if (window.location.hash !== `#${id}`) {
      window.history.replaceState(null, '', `#${id}`);
    }

    // Defer the scroll slightly so any micro-layout shifts (button :active
    // states, reactive updates, etc.) have settled before we measure.
    setTimeout(() => scrollToElementById(id), 50);
  }

  onMounted(() => {
    nextTick(() => scrollToCurrentHashWithDelay());
  });

  watch(
    () => route.hash,
    (newHash, oldHash) => {
      if (newHash !== oldHash) {
        nextTick(() => scrollToCurrentHashWithDelay());
      }
    },
  );

  return { scrollToSection };
}
